import { Component, Container, HBox, Position, PuzzleButton, PuzzleTextArea } from "puzzle";
import { Popup } from "@progress/kendo-react-popup";
import React, { RefObject } from "react";
import { ANCHOR_POPUP_PROPS, ANCHOR_POPUP_STATE } from "./Types";
import Comments from "../Comments";
import { TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import { COMMENT_ITEM } from "../Types";
import classNames from "classnames";
import LimitedLetterCounter from "../../../../../Submission/sections/Details/TemplateDescription/LimitedLetterCounter";
import _ from "lodash";
import { focusNextElement } from "../../../../../util/Util";

class AnchorPopup extends Component<ANCHOR_POPUP_PROPS, ANCHOR_POPUP_STATE> {
  anchorElement: RefObject<HTMLElement> = React.createRef();
  closeBtnRef: RefObject<PuzzleButton> = React.createRef();
  lastFocusedElement: HTMLElement | null = null;
  popupElement: RefObject<Popup> = React.createRef();

  static defaultProps: ANCHOR_POPUP_PROPS = {
    commentsAvailable: false,
    commentsCount: 0,
    folder: '',
    submissionId: '',
    targetId: 0
  };

  public state: ANCHOR_POPUP_STATE = {
    comment: '',
    commentsData: [],
    showPopup: false
  };

  /**
   * Add new comment
   */
  addCommentHandler = () => {
    const { folder, submissionId, targetId } = this.props;
    const { comment } = this.state;
    let params = {
      activeFilters: {
        folder: folder,
        targetIds: [targetId]
      },
      action: 'SAVED',
      comment: comment
    };
    let notificationData = {
      text: this.translate('commentSaved'),
      icon: true,
      closable: true,
      type: 'success'
    };

    this.dispatch('addComment', '', { params, submissionId, notificationData });
    this.closePopup();
  }

  /**
   * Close popup and remove event listener from document
   */
  closePopup = () => {
    this.eventListenerForMouseDown('removeEventListener');
    this.popupOnClose();
    this.dispatch('toggleScrollOnFilesTab', Position.end, { disableScroll: false });
    this.setState({ comment: '', showPopup: false }, () => {
      setTimeout(this.focusLastElement, 0);
    });
  }

  /**
   * Close popup if textarea is empty. Otherwise, show confirmation dialog
   * @param event
   */
  closePopupIfEmpty = (event: any) => {
    let { comment } = this.state;
    if (!comment) {
      this.closePopup();
    } else {
      event.preventDefault();
      this.eventListenerForMouseDown('removeEventListener');
      this.dispatch('confirmClosingOfAnchorPopup');
    }
  }

  /**
   * Add or remove eventListener for handleOutsideClick
   * @param method
   */
  eventListenerForMouseDown = (method: 'addEventListener' | 'removeEventListener') => {
    document[method]('mousedown', this.handleOutsideClick);
  }

  /**
   * Focus last element
   */
  focusLastElement = () => {
    this.lastFocusedElement?.focus();
  }

  /**
   * Close popup when user click outside of it
   * @param event
   */
  handleOutsideClick = (event: Event) => {
    let element = event.target;
    let popupElement = this.popupElement.current?.element;
    if (popupElement && !popupElement.contains(element)) {
      this.closePopupIfEmpty(event);
    }
  }

  /**
   * Text area change event. Save value to state
   * @param event
   */
  onTextAreaChange = (event: TextAreaChangeEvent) => {
    let { value: comment } = event;

    this.setState({
      comment
    });
  }

  /**
   * Open popup and add event listener to document
   */
  openPopup = (event: React.KeyboardEvent | React.MouseEvent) => {
    this.lastFocusedElement = (event.target as HTMLElement);
    const { commentsCount, submissionId, targetId, moduleId } = this.props;
    if (commentsCount) {
      this.dispatch('loadCommentsData', '', { submissionId, targetId, moduleId });
    } else {
      this.loadCommentsData([]);
    }
  }

  /**
   * Load comments data
   * @param commentsData
   */
  loadCommentsData = (commentsData: COMMENT_ITEM[]) => {
    let parsedComments: COMMENT_ITEM[] = this.parseCommentData(commentsData);

    this.setState({ commentsData: parsedComments, showPopup: true });
    this.eventListenerForMouseDown('addEventListener');
    this.dispatch('toggleScrollOnFilesTab', Position.end, { disableScroll: true });
  }

  /**
   * Parse data from server
   * When action is COMPLETED or DEMOTED and comment is missing, take comment text from previous item
   * @param commentsData
   */
  parseCommentData = (commentsData: COMMENT_ITEM[]) => {
    let commentsLength = commentsData.length;
    let parsedData = [];
    let current;
    let previousCurrent;
    while (commentsLength) {
      commentsLength--;
      current = commentsData[commentsLength];
      previousCurrent = commentsData[commentsLength - 1];
      let promotedWithMandatory = current.action === 'COMPLETED' && current.phaseWithMandatoryPromoteComment;
      let demotedWithMandatory = current.action === 'DEMOTED' && current.phaseWithMandatoryDemoteComment;
      let samePhase = current.phase === previousCurrent?.phase;
      if ((promotedWithMandatory || demotedWithMandatory) && samePhase && !current.comment && previousCurrent?.action === 'SAVED') {
        parsedData.push({
          ...current,
          comment: previousCurrent.comment
        });
        commentsLength--;
      } else {
        parsedData.push(current);
      }
    }
    parsedData = _.reverse(parsedData);

    return parsedData;
  }

  /**
   * Handles key down on popup
   *
   * @param event
   */
  popupOnKeyDown = (event: KeyboardEvent) => {
    const el = this.popupElement.current?.element;
    focusNextElement(el, event);
  };

  /**
   * Removes event listener on popup close
   */
  popupOnClose = () => {
    this.popupElement.current?.element.removeEventListener('keydown', this.popupOnKeyDown)
  };

  /**
   * Adding event listener on popup open and focuses close button
   */
  popupOnOpen = () => {
    let closeButtonElement = this.closeBtnRef.current?.buttonRef.current?.element;
    if (closeButtonElement) {
      closeButtonElement.focus();
    }
    this.popupElement.current?.element.addEventListener('keydown', this.popupOnKeyDown)
  };

  renderBody = () => {
    const { showPopup, comment, commentsData } = this.state;
    const { commentsAvailable, commentsCount, submissionId, targetId } = this.props;

    let buttonCls = showPopup ? 'tp-anchor-popup-open' : '';
    let commentsProps = {
      submissionId,
      targetId,
      data: commentsData
    }
    let showComments = !!commentsCount || commentsAvailable;
    let buttonIconClasses = classNames(
      'tp-icon',
      'tp-icon-sm', {
        'tp-file-comments': commentsCount,
        'tp-add-comment': !commentsCount
      }
    );

    return (
      <Container className={`tp-popup-wrapper ${buttonCls}`} ref={this.anchorElement}>
        {showComments && (
          <>
            <PuzzleButton
              iconClass={buttonIconClasses}
              fillMode={'flat'}
              onClick={this.openPopup}
            />
            <span className={'tp-color-link'}>
              {commentsCount ? commentsCount : null}
            </span>
            <Popup
              anchor={this.anchorElement.current}
              anchorAlign={{
                horizontal: 'left',
                vertical: 'center'
              }}
              collision={{
                horizontal: 'fit',
                vertical: "fit",
              }}
              onOpen={this.popupOnOpen}
              popupAlign={{
                horizontal: 'right',
                vertical: 'center'
              }}
              popupClass={'tp-anchor-popup-container'}
              ref={this.popupElement}
              show={showPopup}
            >
              <HBox
                alignItemsCenter
                className={'tp-popup-header'}
                justifyContentBetween
              >
                <h2 className={'tp-popup-title'}>
                  {this.translate('fileComments')}
                </h2>
                <PuzzleButton
                  ref={this.closeBtnRef}
                  fillMode="flat"
                  iconClass={"tp-icon tp-x"}
                  onClick={this.closePopupIfEmpty}
                />
              </HBox>
              <Comments {...commentsProps} />
              {commentsAvailable &&
                <>
                  <PuzzleTextArea
                    maxLength={1000}
                    onChange={this.onTextAreaChange}
                    placeholder={this.translate('textAreaPlaceholder')}
                    withSendButton
                    value={comment}
                    withSendButtonConfig={{
                      fillMode: 'flat',
                      iconClass: 'tp-icon tp-restore',
                      onClick: this.addCommentHandler,
                      disabled: !comment
                    }}
                  />
                  <LimitedLetterCounter
                    className={'tp-textarea-counter tp-color-light'}
                    stringToCount={comment}
                    maxCharacters={1000}
                  />
                </>}
            </Popup>
          </>
        )}
      </Container>
    );
  }
}

export default AnchorPopup;
